import { zodResolver } from '@hookform/resolvers/zod';
import { ambientesSchema, AMBIENTES_PADRAO, type Ambientes } from '@plane/shared';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { Field } from '@/components/field';
import { CampoDeImagem } from '@/modules/site-content/components/campo-de-imagem';
import { SeletorDeAncora } from '@/modules/site-content/components/seletor-de-ancora';
import { useAmbientes, useSaveAmbientes } from '@/modules/site-content/hooks/use-conteudo';
import { ApiRequestError } from '@/services/http-client';

function mensagemDeErro(erro: unknown): string {
  if (erro instanceof ApiRequestError) return erro.message;
  if (erro instanceof Error) return erro.message;

  return 'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.';
}

export function AmbientesPage() {
  const consulta = useAmbientes();
  const salvar = useSaveAmbientes();
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  if (consulta.isPending) return <p className="text-sm text-ink-soft">Carregando…</p>;

  if (consulta.isError) {
    return (
      <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
        {mensagemDeErro(consulta.error)}
      </p>
    );
  }

  return (
    <Formulario
      key={consulta.data ? 'salvo' : 'padrao'}
      inicial={consulta.data ?? AMBIENTES_PADRAO}
      inedito={consulta.data === null}
      erro={erro}
      salvo={salvo}
      salvando={salvar.isPending}
      aoEnviar={(ambientes) => {
        setErro(null);
        setSalvo(false);

        salvar
          .mutateAsync(ambientes)
          .then(() => setSalvo(true))
          .catch((falha: unknown) => setErro(mensagemDeErro(falha)));
      }}
    />
  );
}

function Formulario({
  inicial,
  inedito,
  erro,
  salvo,
  salvando,
  aoEnviar,
}: {
  inicial: Ambientes;
  inedito: boolean;
  erro: string | null;
  salvo: boolean;
  salvando: boolean;
  aoEnviar: (ambientes: Ambientes) => void;
}) {
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Ambientes>({
    resolver: zodResolver(ambientesSchema),
    defaultValues: inicial,
  });

  const itens = useFieldArray({ control, name: 'itens' });
  const valores = watch('itens');

  return (
    <form
      onSubmit={(evento) => {
        void handleSubmit(aoEnviar)(evento);
      }}
      className="flex max-w-2xl flex-col gap-6"
    >
      <div>
        <p className="text-xs tracking-[0.18em] text-brass uppercase">Conteúdo do site</p>
        <h1 className="mt-2 font-display text-2xl">Ambientes</h1>
        <p className="mt-3 text-sm text-ink-soft">
          A lista escura, na segunda seção. A foto de cada linha é a miniatura que acompanha o
          cursor de quem passa o mouse por ela.
        </p>
      </div>

      {inedito && (
        <p className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          Esta seção ainda não foi salva. Os textos abaixo vêm do protótipo e as fotos estão vazias
          — sem foto a linha continua acendendo, só a miniatura não aparece.
        </p>
      )}

      <Field
        id="kicker"
        label="Etiqueta acima do título"
        placeholder="02 — Ambientes"
        error={errors.kicker?.message}
        {...register('kicker')}
      />

      <div className="flex flex-col gap-1.5">
        <label htmlFor="titulo" className="text-xs tracking-[0.12em] text-ink-soft uppercase">
          Título
        </label>
        <textarea
          id="titulo"
          rows={2}
          aria-invalid={errors.titulo ? true : undefined}
          aria-describedby="titulo-ajuda"
          className="border border-clay bg-white px-3 py-2.5 text-sm transition-colors focus:border-brand-600 focus:outline-none aria-invalid:border-brass"
          {...register('titulo')}
        />
        <p id="titulo-ajuda" className="text-xs text-ink-soft">
          Quebre a linha onde o título deve quebrar. <code>*assim*</code> destaca a palavra em
          dourado itálico.
        </p>
        {errors.titulo && (
          <p role="alert" className="text-xs text-brass">
            {errors.titulo.message}
          </p>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Field
            id="dica"
            label="Dica no canto"
            placeholder="passe o mouse"
            error={errors.dica?.message}
            {...register('dica')}
          />
          <p className="mt-1.5 text-xs text-ink-soft">
            Some em celular e tablet. Deixe vazio para escondê-la sempre.
          </p>
        </div>

        <div>
          <SeletorDeAncora
            id="ancora"
            rotulo="Cada linha leva para"
            registro={register('ancora')}
          />
        </div>
      </div>

      <fieldset className="m-0 border-0 p-0">
        <legend className="text-xs tracking-[0.12em] text-ink-soft uppercase">Ambientes</legend>
        <p className="mt-1.5 text-xs text-ink-soft">
          A numeração (01, 02…) sai da ordem da lista — não é campo.
        </p>

        <ol className="mt-4 flex list-none flex-col gap-6 p-0">
          {itens.fields.map((campo, indice) => (
            <li key={campo.id} className="flex flex-col gap-4 border-l-2 border-sand pl-4">
              <div className="flex items-center justify-between">
                <span className="font-display text-lg text-brand-600 tabular-nums">
                  {String(indice + 1).padStart(2, '0')}
                </span>

                <div className="flex items-center gap-3 text-xs">
                  <button
                    type="button"
                    onClick={() => itens.move(indice, indice - 1)}
                    disabled={indice === 0}
                    aria-label={`Subir o ambiente ${String(indice + 1)}`}
                    className="text-brand-700 underline disabled:text-ink-soft/40 disabled:no-underline"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => itens.move(indice, indice + 1)}
                    disabled={indice === itens.fields.length - 1}
                    aria-label={`Descer o ambiente ${String(indice + 1)}`}
                    className="text-brand-700 underline disabled:text-ink-soft/40 disabled:no-underline"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => itens.remove(indice)}
                    disabled={itens.fields.length === 1}
                    className="text-brass underline disabled:text-ink-soft/40 disabled:no-underline"
                  >
                    Remover
                  </button>
                </div>
              </div>

              <Field
                id={`ambiente-nome-${String(indice)}`}
                label="Nome"
                placeholder="Cozinhas"
                error={errors.itens?.[indice]?.nome?.message}
                {...register(`itens.${indice}.nome`)}
              />

              <div>
                <Field
                  id={`ambiente-detalhe-${String(indice)}`}
                  label="Detalhe"
                  placeholder="torre quente · gavetão · nichos de embutir"
                  error={errors.itens?.[indice]?.detalhe?.message}
                  {...register(`itens.${indice}.detalhe`)}
                />
                <p className="mt-1.5 text-xs text-ink-soft">
                  Some em telas estreitas — o essencial é o nome.
                </p>
              </div>

              <div>
                <p className="text-xs tracking-[0.12em] text-ink-soft uppercase">
                  Foto da miniatura
                </p>
                <div className="mt-2">
                  <CampoDeImagem
                    mediaId={valores[indice]?.foto ?? null}
                    aoTrocar={(id) =>
                      setValue(`itens.${indice}.foto`, id, { shouldValidate: true })
                    }
                  />
                </div>
              </div>
            </li>
          ))}
        </ol>

        {itens.fields.length < 12 && (
          <button
            type="button"
            onClick={() => itens.append({ nome: '', detalhe: '', foto: null })}
            className="mt-4 text-sm text-brand-700 underline"
          >
            Adicionar ambiente
          </button>
        )}
      </fieldset>

      {erro && (
        <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          {erro}
        </p>
      )}

      {salvo && (
        <p role="status" className="border-l-2 border-brand-600 pl-3 text-sm text-brand-700">
          Salvo. O site já mostra o conteúdo novo.
        </p>
      )}

      <button
        type="submit"
        disabled={salvando}
        className="self-start bg-brand-600 px-5 py-2.5 text-sm text-white transition-opacity disabled:opacity-50"
      >
        {salvando ? 'Salvando…' : 'Salvar'}
      </button>
    </form>
  );
}
